import { useEffect, useMemo, useState } from 'react';
import './ActivityPage.css';

const eventStart = new Date('2026-07-30T23:59:59+08:00').getTime();
const eventEnd = new Date('2026-08-15T23:59:00+08:00').getTime();

const rewards = [
  ['01', '以太 ×500', 'UU物质 ×64', '工业全套设备礼包 ×1'],
  ['02', '以太 ×300', 'UU物质 ×32', '电子电路 ×5'],
  ['03', '以太 ×500', 'UU物质 ×48', '高级纳米聚合体 ×2'],
  ['04', '以太 ×300', 'UU物质 ×32', '空岛资源补给箱 ×1'],
  ['05', '以太 ×500', 'UU物质 ×48', '空岛食材自选箱 ×1'],
  ['06', '以太 ×300', 'UU物质 ×32', '电动马达 ×3'],
  ['07', '以太 ×500', 'UU物质 ×64'],
  ['08', '以太 ×300', 'UU物质 ×32', '高级暗物质线束 ×2'],
  ['09', '以太 ×500', 'UU物质 ×48', '高级量子刃晶 ×2'],
  ['10', '以太 ×300', 'UU物质 ×32'],
  ['11', '以太 ×500', 'UU物质 ×48'],
  ['12', '以太 ×300', 'UU物质 ×32', '高级脉冲核芯 ×2'],
  ['13', '以太 ×500', 'UU物质 ×48', '高级星锻合金 ×2'],
  ['14', '以太 ×500', 'UU物质 ×64'],
  ['15', '以太 ×500', 'UU物质 ×96', '夏日珍稀资源箱 ×1'],
];

const weapons = [
  { type: '近战 · 熔岩系', name: '余烬重锤', en: 'Embershot Hammer', desc: '7 秒冷却 · 地震波与径向熔岩喷射', mark: '焰' },
  { type: '远程 · 光谱系', name: '极光弓', en: 'Aurora Splitter', desc: '贝塞尔曲线分裂追踪箭', mark: '弧' },
  { type: '法术 · 冰霜系', name: '寒冬吐息', en: 'Glacier Breath', desc: '扇形冰息与速度冻结', mark: '霜' },
  { type: '法术 · 陨石系', name: '流星陨落', en: 'Meteor Pact', desc: '陨石坠落与镜头震动', mark: '陨' },
];

const qqGroups = [
  { name: '官方1群', number: '616444639', link: 'https://qm.qq.com/q/juPbcm3eBa' },
  { name: '官方2群', number: '874462523', link: 'https://qm.qq.com/q/MTyzQHvBG6' },
  { name: '官方3群', number: '182670005', link: 'https://qm.qq.com/q/c6lUVm39fO' },
  { name: '官方4群', number: '531581648', link: 'https://qm.qq.com/q/Wpegbnzr0o' },
  { name: '官方5群', number: '339605679', link: 'https://qm.qq.com/q/kIN0OFP6fK' },
  { name: '官方6群', number: '414554298', link: 'https://qm.qq.com/q/v8vGHbQNBm' },
];

function getCountdown(now) {
  if (now >= eventEnd) return { label: '活动已结束', value: '感谢同行', state: 'ended' };
  if (now >= eventStart) return { label: '暑期活动进行中', value: '立即登录领取奖励', state: 'live' };

  const gap = eventStart - now;
  const days = Math.floor(gap / 86400000);
  const hours = Math.floor((gap % 86400000) / 3600000);
  const minutes = Math.floor((gap % 3600000) / 60000);
  const seconds = Math.floor((gap % 60000) / 1000);
  return {
    label: '距离活动开启',
    value: `${String(days).padStart(2, '0')}天 ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
    state: 'counting',
  };
}

function ActivityPage() {
  const [now, setNow] = useState(Date.now());
  const [navOpen, setNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('story');
  const countdown = useMemo(() => getCountdown(now), [now]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    const oldTitle = document.title;
    const oldDescription = document.querySelector('meta[name="description"]')?.getAttribute('content');
    const description = document.querySelector('meta[name="description"]');
    document.title = '蓝星叙事：空岛番外篇前瞻｜地球天空';
    description?.setAttribute('content', '蓝星叙事：空岛番外篇前瞻。探索全新空岛叙事、芯片工业、料理、特别武器与伙伴系统。');

    const setMeta = (property, content) => {
      let node = document.querySelector(`meta[property="${property}"]`);
      if (!node) {
        node = document.createElement('meta');
        node.setAttribute('property', property);
        document.head.appendChild(node);
      }
      node.setAttribute('content', content);
    };
    setMeta('og:title', '蓝星叙事：空岛番外篇前瞻');
    setMeta('og:description', '归巢，是让相隔数百年的两边，终于重新听见彼此。');
    setMeta('og:image', `${window.location.origin}${process.env.PUBLIC_URL}/og-back-to-earth.png`);

    return () => {
      window.clearInterval(timer);
      document.title = oldTitle;
      if (oldDescription) description?.setAttribute('content', oldDescription);
    };
  }, []);

  useEffect(() => {
    const root = document.querySelector('.event-page');
    if (!root) return undefined;

    const revealTargets = [
      ...root.querySelectorAll(
        '.story-grid > *, .section-heading > *, .reward-intro, .reward-totals, .milestones, .reward-details, ' +
        '.flow-panel, .production-grid > *, .life-copy, .kitchen-grid > *, .weapon-grid > *, ' +
        '.companion-signal, .companion-copy, .schedule-main, .compensation-card, .footer-container, .footer-bottom'
      ),
    ];
    const staggerGroups = root.querySelectorAll(
      '.update-grid, .reward-totals, .milestones, .chip-flow, .production-grid, .kitchen-grid, .weapon-grid'
    );

    revealTargets.forEach((element) => element.classList.add('reveal-item'));
    staggerGroups.forEach((group) => {
      [...group.children].forEach((element, index) => {
        element.classList.add('reveal-item');
        element.style.setProperty('--reveal-delay', `${Math.min(index * 75, 375)}ms`);
      });
    });
    const allRevealTargets = [...root.querySelectorAll('.reveal-item')];
    root.classList.add('motion-ready');

    let revealObserver;
    if ('IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -8% 0px' });
      allRevealTargets.forEach((element) => revealObserver.observe(element));
    } else {
      allRevealTargets.forEach((element) => element.classList.add('is-revealed'));
    }

    let sectionObserver;
    if ('IntersectionObserver' in window) {
      sectionObserver = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target.id) setActiveSection(visible.target.id);
      }, { threshold: [0.12, 0.3, 0.55], rootMargin: '-20% 0px -58% 0px' });

      ['story', 'updates', 'rewards', 'schedule'].forEach((id) => {
        const section = document.getElementById(id);
        if (section) sectionObserver.observe(section);
      });
    }

    let frame = 0;
    const updateScrollEffects = () => {
      const y = window.scrollY;
      const maxScroll = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      root.style.setProperty('--scroll-progress', String(Math.min(y / maxScroll, 1)));
      root.style.setProperty('--hero-shift', String(Math.min(y * 0.13, 110)));
      root.style.setProperty('--hero-fade', String(Math.min(y / Math.max(window.innerHeight * 0.82, 1), 1)));
      root.style.setProperty('--ambient-y', String(Math.min(y * 0.035, 120)));
      root.querySelector('.event-nav')?.classList.toggle('is-scrolled', y > 36);
      frame = 0;
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(updateScrollEffects);
    };
    updateScrollEffects();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      revealObserver?.disconnect();
      sectionObserver?.disconnect();
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const closeNav = () => setNavOpen(false);

  return (
    <main className="event-page">
      <div className="event-scroll-progress" aria-hidden="true"><i /></div>
      <div className="event-atmosphere" aria-hidden="true">
        <span className="atmosphere-orb atmosphere-orb-one" />
        <span className="atmosphere-orb atmosphere-orb-two" />
        <span className="atmosphere-orb atmosphere-orb-three" />
      </div>
      <nav className="event-nav" aria-label="活动页导航">
        <a className="event-brand" href="/" aria-label="返回地球天空官网">
          <img src={`${process.env.PUBLIC_URL}/assets/logo.png`} alt="" />
        </a>
        <button
          className="event-nav-toggle"
          type="button"
          aria-expanded={navOpen}
          aria-controls="event-nav-links"
          onClick={() => setNavOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
          <b className="sr-only">打开导航</b>
        </button>
        <div id="event-nav-links" className={`event-nav-links ${navOpen ? 'is-open' : ''}`}>
          <a className={activeSection === 'story' ? 'is-active' : ''} href="#story" onClick={closeNav}>剧情前瞻</a>
          <a className={activeSection === 'updates' ? 'is-active' : ''} href="#updates" onClick={closeNav}>版本内容</a>
          <a className={activeSection === 'rewards' ? 'is-active' : ''} href="#rewards" onClick={closeNav}>活动福利</a>
          <a className={activeSection === 'schedule' ? 'is-active' : ''} href="#schedule" onClick={closeNav}>维护安排</a>
        </div>
      </nav>

      <header className="event-hero">
        <div
          className="event-hero-image"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/back-to-earth.png)` }}
          role="img"
          aria-label="蓝星叙事空岛番外篇：漂浮岛屿、轨道与远方的蓝色地球"
        />
        <div className="event-hero-shade" />
        <div className="event-hero-meta">
          <span className="eyebrow"><i /> BLUE PLANET ARCHIVE · 07</span>
          <span className="hero-status">RPG 玩法前瞻 · 制作中</span>
        </div>
        <div className={`countdown-panel ${countdown.state}`}>
          <span>{countdown.label}</span>
          <strong>{countdown.value}</strong>
          <small>2026.07.30 — 2026.08.15</small>
        </div>
        <a className="hero-scroll" href="#story">
          <span>接收信号</span>
          <i />
        </a>
      </header>

      <section className="event-section story-section" id="story">
        <div className="section-index">01 / SIGNAL RECEIVED</div>
        <div className="story-grid">
          <div>
            <p className="eyebrow">蓝星叙事 · 空岛番外篇</p>
            <h1>那一天，来自下方的信号<br />穿过云层。</h1>
          </div>
          <div className="story-copy">
            <p>灾厄之后，一部分土地被送入天空。数百年间，人们在浮空岛建立家园，并将脚下那颗蓝色星球称作“故土”。</p>
            <p>如今，沉寂的航路再次开启。你将以新登记行动员的身份从第七浮岛苍庭镇出发，见证接触舰“归巢一号”抵达空岛。</p>
            <blockquote>归巢不是把谁接走。<br />归巢，是让相隔数百年的两边，终于重新听见彼此。</blockquote>
            <p className="development-note">本次为前瞻更新，真正的 RPG 玩法、章节与伙伴内容仍在制作中，将于后续陆续公开。</p>
          </div>
        </div>
      </section>

      <section className="event-section update-section" id="updates">
        <div className="section-heading">
          <div>
            <span className="section-index">02 / VERSION OVERVIEW</span>
            <p className="eyebrow">版本更新速递</p>
            <h2>空岛生活，重新编译</h2>
          </div>
          <p>从生存生产到角色同行，这次前瞻展示空岛世界下一阶段的基础模块。</p>
        </div>

        <div className="update-grid">
          <article className="update-card update-card-wide">
            <span className="card-number">01</span>
            <div className="card-mark">芯</div>
            <div>
              <small>CHIP INDUSTRY</small>
              <h3>芯片工业</h3>
              <p>从晶圆制造、EUV 刻蚀到封装交付，建立完整的高级芯片产线。芯片可承接订单，也可升级工业设备。</p>
            </div>
          </article>
          <article className="update-card">
            <span className="card-number">02</span>
            <div className="card-mark">作</div>
            <small>FARM & COOK</small>
            <h3>农作与烹饪</h3>
            <p>新增水稻、红薯、玉米与橡胶树。58 道菜、60 种食材和 6 类厨具完善空岛饮食生态。</p>
          </article>
          <article className="update-card">
            <span className="card-number">03</span>
            <div className="card-mark">工</div>
            <small>AUTOMATION</small>
            <h3>工业自动化</h3>
            <p>矿晶源与粗矿粉可组成循环生产线；磨粉机、高炉等常用设备完成单方块小型化。</p>
          </article>
          <article className="update-card">
            <span className="card-number">04</span>
            <div className="card-mark">武</div>
            <small>ARSENAL</small>
            <h3>特别武器</h3>
            <p>熔岩、光谱、冰霜与陨石四种战斗风格即将加入，部分武器将在夏季活动中免费赠送。</p>
          </article>
          <article className="update-card">
            <span className="card-number">05</span>
            <div className="card-mark">伴</div>
            <small>COMPANION</small>
            <h3>伙伴系统</h3>
            <p>伙伴将自主追击、掩护、治疗与支援，并以连携技和终结技回应你的战斗节奏。</p>
          </article>
          <a className="update-card update-link-card" href="/wiki">
            <span className="card-number">06</span>
            <div className="card-mark">册</div>
            <small>CRAFTING WIKI</small>
            <h3>全新网页百科</h3>
            <p>配方、流程图与建筑胶囊系统指南集中上线。</p>
            <b>前往地空工艺 Wiki ↗</b>
          </a>
        </div>
      </section>

      <section className="event-section rewards-section" id="rewards">
        <div className="reward-intro">
          <span className="section-index">03 / SUMMER SUPPLY</span>
          <p className="eyebrow">暑期签到礼赠</p>
          <h2>15 日补给航线</h2>
          <p>全服通用以太与空岛专属物资同步发放。每日签到，逐步补齐工业、芯片与珍稀资源。</p>
        </div>
        <div className="reward-totals">
          <div><strong>12,800</strong><span>以太 · 含累计奖励</span></div>
          <div><strong>720</strong><span>UU 物质</span></div>
          <div><strong>3 / 7 / 15</strong><span>三档累计里程碑</span></div>
        </div>

        <div className="milestones">
          <div><b>累计 3 天</b><span>以太 ×1500</span><span>机器升级芯片 ×1</span></div>
          <div><b>累计 7 天</b><span>以太 ×2500</span><span>发电机升级芯片 ×1 · 储能升级芯片 ×1</span></div>
          <div><b>累计 15 天</b><span>以太 ×2500</span><span>超级纳米聚合体 ×2 · 量子火控芯片 ×1</span></div>
        </div>

        <details className="reward-details">
          <summary>查看 15 日每日奖励明细 <span>+</span></summary>
          <div className="reward-list">
            {rewards.map(([day, ether, uu, item]) => (
              <div className="reward-row" key={day}>
                <strong>DAY {day}</strong>
                <span>{ether}</span>
                <span>{uu}</span>
                <span>{item || '—'}</span>
              </div>
            ))}
          </div>
        </details>
      </section>

      <section className="event-section production-section">
        <div className="section-heading">
          <div>
            <span className="section-index">04 / PRODUCTION LOOP</span>
            <p className="eyebrow">芯片 · 矿业 · 自动化</p>
            <h2>把复杂，交给产线</h2>
          </div>
          <p>免费领取 64 个基础芯片起步；进阶生产则从石英与晶圆开始。</p>
        </div>

        <div className="flow-panel">
          <div className="flow-title"><span>高级芯片制作流程</span><small>ADVANCED CHIP FABRICATION</small></div>
          <div className="chip-flow">
            {['石英', '石英砂', '单晶硅锭', '晶圆', 'EUV 刻蚀', '芯片封装'].map((step, index) => (
              <div className="flow-step" key={step}>
                <i>{String(index + 1).padStart(2, '0')}</i>
                <strong>{step}</strong>
                {index < 5 && <span>→</span>}
              </div>
            ))}
          </div>
          <p>刻蚀需支付新大陆币，并有概率获得不同等级的晶圆产物；配合高级光刻胶与高级芯片电路完成封装。</p>
        </div>

        <div className="production-grid">
          <article>
            <span className="loop-badge">AUTO LOOP</span>
            <h3>矿物循环生产</h3>
            <p>提取机将粗矿粉制成矿晶源，矿物培养机再产出矿粉，形成可持续的自动矿物产线。</p>
            <div className="loop-diagram">
              <span>粗矿粉</span><b>提取机</b><span>矿晶源</span><b>培养机</b>
            </div>
            <small>覆盖铜、锡、铝、铀、银、铁、金 7 类矿物</small>
          </article>
          <article>
            <span className="loop-badge">COMPACT</span>
            <h3>设备小型化</h3>
            <p>磨粉机、高炉、马弗炉与电炉等常用设备改为单一方块，释放更多岛屿空间。</p>
            <div className="machine-line">
              {['磨', '炉', '冶', '电'].map((item) => <i key={item}>{item}</i>)}
            </div>
            <small>核电站反应堆核心、冷却塔与蒸汽轮机保留大型结构</small>
          </article>
        </div>
      </section>

      <section className="event-section life-section">
        <div className="life-copy">
          <span className="section-index">05 / ISLAND LIFE</span>
          <p className="eyebrow">新的作物与烹饪方式</p>
          <h2>让空岛长出<br />故乡的味道</h2>
          <p>空岛集市带来水稻、红薯、玉米与橡胶树。炒、蒸、煮、烤、搅拌、凉拌，六类厨具让 58 道菜品拥有各自的制作逻辑。</p>
          <a href="https://shimo.im/sheets/2wAldgomYbUNl2AP/52unw" target="_blank" rel="noopener noreferrer">
            查看空岛集市货品表 ↗
          </a>
        </div>
        <div className="kitchen-grid" aria-label="新增厨具与示例菜品">
          {[
            ['炒锅', '玉米 + 糖', '玉米烙'],
            ['蒸锅', '红豆 + 糖', '红豆糕'],
            ['煮锅', '米', '米饭'],
            ['烤箱', '番薯', '熟番薯'],
            ['搅拌机', '蒜 + 食盐', '蒜蓉酱'],
            ['砧板', '生菜 + 蒜 + 糖', '凉拌生菜'],
          ].map(([tool, materials, result], index) => (
            <div key={tool}>
              <i>{String(index + 1).padStart(2, '0')}</i>
              <strong>{tool}</strong>
              <span>{materials}</span>
              <b>→ {result}</b>
            </div>
          ))}
        </div>
      </section>

      <section className="event-section weapons-section">
        <div className="section-heading">
          <div>
            <span className="section-index">06 / SPECIAL ARSENAL</span>
            <p className="eyebrow">多种特别武器即将推出</p>
            <h2>四种回应，划破云层</h2>
          </div>
          <p>正式更新后将开放定期 UP 卡池，部分武器会在夏季活动期间免费赠送。</p>
        </div>
        <div className="weapon-grid">
          {weapons.map((weapon) => (
            <article key={weapon.name}>
              <div className="weapon-mark">{weapon.mark}</div>
              <small>{weapon.type}</small>
              <h3>{weapon.name}</h3>
              <em>{weapon.en}</em>
              <p>{weapon.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="companion-section">
        <div className="companion-signal" aria-hidden="true">
          <strong>07</strong>
          <span>同行协议</span>
          <small>COMPANION / PREVIEW</small>
        </div>
        <div className="companion-copy">
          <span className="section-index">07 / COMPANION PROTOCOL</span>
          <p className="eyebrow">伙伴系统 · 开发中</p>
          <h2>这一次，回应你的<br />不再只有回声。</h2>
          <p>伙伴不是替你战斗的工具。他们会观察战场、选择目标，并依据自身定位进行追击、掩护、治疗、控制与设备支援。</p>
          <div className="companion-tags">
            <span>自主战斗</span><span>专属连携技</span><span>终结技</span><span>编队与潜能</span><span>选择回应</span>
          </div>
          <blockquote>他们有各自想要守住的东西。<br />只是从某一天开始，你们的航线暂时重合。</blockquote>
          <small>伙伴档案、战斗演示、培养功能与开放安排将在后续前瞻逐步公开。</small>
        </div>
      </section>

      <section className="event-section schedule-section" id="schedule">
        <div className="schedule-main">
          <span className="section-index">08 / OPERATION NOTICE</span>
          <p className="eyebrow">维护与活动安排</p>
          <h2>航路重启时间表</h2>
          <div className="schedule-list">
            <div>
              <time>07.30 <small>23:00</small></time>
              <span><b>停机维护开始</b>部分服务器暂时无法登录</span>
            </div>
            <div>
              <time>07.30 <small>23:59</small></time>
              <span><b>停机维护结束</b>如遇特殊情况，以实际开服时间为准</span>
            </div>
            <div>
              <time>07.30 <small>23:59</small></time>
              <span><b>暑期活动开启</b>持续至 08.15 23:59</span>
            </div>
          </div>
        </div>
        <aside className="compensation-card">
          <span>维护补偿</span>
          <h3>感谢每一次等待</h3>
          <div><strong>10,000</strong><small>以太</small></div>
          <div><strong>1,600</strong><small>UU 物质 · 空岛专属</small></div>
          <p>游戏大厅等级大于 10 的玩家，可在开服后通过系统邮件领取。邮件有效期 90 天。</p>
        </aside>
      </section>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-logo">
            <img src={`${process.env.PUBLIC_URL}/assets/big_logo.png`} alt="地球天空" />
          </div>
          <div className="footer-content">
            <div className="footer-left">
              <p className="footer-slogan">地球天空运营组致力于为您带来快乐！</p>
              <p className="footer-tagline">专业 / 效率 / 温情</p>
            </div>
            <div className="footer-right">
              <h4 className="footer-section-title">玩家社群</h4>
              <div className="footer-groups">
                {qqGroups.map((group) => (
                  <a
                    key={group.number}
                    href={group.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-group-row"
                  >
                    <span className="group-name">地球天空{group.name}：</span>
                    <span className="group-id">{group.number}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <span className="footer-copyright">© {new Date().getFullYear()} 地球天空 SkyPixel. All rights reserved.</span>
          <span className="footer-divider">|</span>
          <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer" className="footer-icp">
            粤ICP备15051067号-1
          </a>
        </div>
      </footer>
    </main>
  );
}

export default ActivityPage;
